import { memo, useCallback, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip";

import useContainerSize from "hooks/useContainerSize";
import Box from "ds/components/Box";

import styles from "./styles.module.css";
import ChartTooltip from "../components/Tooltip";
import { tooltipContainerStyles } from "../components/Tooltip/helpers";
import {
  AXIS_DOT_FILL_OPACITY,
  AXIS_DOT_RADIUS,
  DEFAULT_MAX_VALUE,
  FALLBACK_SIZE,
  GRID_FILL_OPACITY_MULTIPLIER,
  GRID_LEVELS,
  GRID_STROKE_WIDTH,
  HOVER_TARGET_STROKE_WIDTH,
  PRIMARY_POLYGON_FILL_OPACITY,
  PRIMARY_POLYGON_FILL_OPACITY_HOVERED,
  PRIMARY_POLYGON_STROKE_OPACITY,
  PRIMARY_POLYGON_STROKE_WIDTH,
  SECONDARY_POLYGON_FILL_OPACITY,
  SECONDARY_POLYGON_FILL_OPACITY_HOVERED,
  SECONDARY_POLYGON_STROKE_OPACITY,
  SECONDARY_POLYGON_STROKE_WIDTH,
  TOOLTIP_OFFSET,
} from "./constants";
import useRadarChartGeometry from "./useGeometry";
import type { RadarChartProps, RadarDatum } from "./types";

const tooltipStyles = { ...defaultStyles, ...tooltipContainerStyles };
const tooltipLeftStyles = { ...tooltipStyles, transform: "translateX(-100%)" };

const RadarChart = ({
  data,
  secondaryData, // It needs to be the same length as the data
  isLoading = false,
  maxValue = DEFAULT_MAX_VALUE,
  fillColor = "var(--level-chart-3)",
  strokeColor = "var(--level-chart-4)",
  secondaryFillColor,
  secondaryStrokeColor,
  gridColor = "var(--semantic-color-border-divider)",
  gridLevels = GRID_LEVELS,
  renderTooltip,
  tooltipReactToScroll,
  tooltipPlacement = "right",
  showTooltipCallback,
  hideTooltipCallback,
  showAxisDots = false,
}: RadarChartProps) => {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const svgRef = useRef<SVGSVGElement>(null);
  const { containerRef, width, height } = useContainerSize();
  const measuredSize = Math.min(width, height || width);
  const size = measuredSize > 0 ? measuredSize : FALLBACK_SIZE;

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<RadarDatum>();

  const {
    containerRef: tooltipContainerRef,
    containerBounds,
    TooltipInPortal,
  } = useTooltipInPortal({
    scroll: tooltipReactToScroll,
  });

  const {
    cx,
    cy,
    polygonPoints,
    secondaryPolygonPoints,
    dataPoints,
    secondaryDataPoints,
    gridPaths,
    axisLines,
  } = useRadarChartGeometry(data, size, maxValue, gridLevels, secondaryData);

  const showTooltipForIndex = useCallback(
    (index: number, clientX: number, clientY: number) => {
      const datum = data[index];
      if (datum) {
        setActiveIndex(index);
        showTooltip({
          tooltipData: datum,
          tooltipLeft: clientX - (containerBounds.left ?? 0),
          tooltipTop: clientY - (containerBounds.top ?? 0),
        });
        showTooltipCallback?.(datum);
      }
    },
    [data, showTooltip, showTooltipCallback, containerBounds.left, containerBounds.top]
  );

  const clearActiveIndex = useCallback(() => {
    setActiveIndex(undefined);
    hideTooltip();
    hideTooltipCallback?.();
  }, [hideTooltip, hideTooltipCallback]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!data.length) return;

      let nextIndex: number | undefined;

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        nextIndex = activeIndex === undefined ? 0 : (activeIndex + 1) % data.length;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        nextIndex =
          activeIndex === undefined
            ? data.length - 1
            : (activeIndex - 1 + data.length) % data.length;
      } else if (e.key === "Escape") {
        clearActiveIndex();
        return;
      }

      if (nextIndex !== undefined) {
        const point = dataPoints[nextIndex];
        const svgRect = svgRef.current?.getBoundingClientRect();
        if (point && svgRect) {
          showTooltipForIndex(nextIndex, svgRect.left + point.x, svgRect.top + point.y);
        }
      }
    },
    [data.length, activeIndex, dataPoints, showTooltipForIndex, clearActiveIndex]
  );

  // TODO: empty state
  if (!isLoading && !data.length) return null;

  if (isLoading) {
    return (
      <Box ref={containerRef} fullWidth className={styles.container}>
        <Skeleton circle width={size} height={size} />
      </Box>
    );
  }

  return (
    <Box ref={containerRef} fullWidth className={styles.container}>
      <div
        ref={tooltipContainerRef}
        className={styles.tooltipPortalContainer}
        style={{ width: size, height: size }}
      >
        <svg
          ref={svgRef}
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className={styles.svg}
          role="group"
          aria-label="Radar chart. Use arrow keys to navigate data points."
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onBlur={clearActiveIndex}
        >
          <g>
            {gridPaths.map((points, i) => {
              const level = gridLevels - i;
              return (
                <polygon
                  key={level}
                  points={points}
                  fill={gridColor}
                  fillOpacity={(level / gridLevels) * GRID_FILL_OPACITY_MULTIPLIER}
                  stroke={gridColor}
                  strokeWidth={GRID_STROKE_WIDTH}
                  className={styles.gridPolygon}
                />
              );
            })}
            {axisLines.map((line, i) => (
              <line
                key={i}
                x1={cx}
                y1={cy}
                x2={line.x2}
                y2={line.y2}
                stroke={activeIndex === i ? strokeColor : gridColor}
                strokeWidth={activeIndex === i ? PRIMARY_POLYGON_STROKE_WIDTH : GRID_STROKE_WIDTH}
                className={styles.axisLine}
              />
            ))}
            {secondaryPolygonPoints && (
              <polygon
                points={secondaryPolygonPoints}
                fill={secondaryFillColor ?? fillColor}
                fillOpacity={
                  activeIndex !== undefined
                    ? SECONDARY_POLYGON_FILL_OPACITY_HOVERED
                    : SECONDARY_POLYGON_FILL_OPACITY
                }
                stroke={secondaryStrokeColor ?? strokeColor}
                strokeOpacity={SECONDARY_POLYGON_STROKE_OPACITY}
                strokeWidth={SECONDARY_POLYGON_STROKE_WIDTH}
                className={styles.dataPolygon}
              />
            )}
            <polygon
              points={polygonPoints}
              fill={fillColor}
              fillOpacity={
                activeIndex !== undefined
                  ? PRIMARY_POLYGON_FILL_OPACITY_HOVERED
                  : PRIMARY_POLYGON_FILL_OPACITY
              }
              stroke={strokeColor}
              strokeOpacity={PRIMARY_POLYGON_STROKE_OPACITY}
              strokeWidth={PRIMARY_POLYGON_STROKE_WIDTH}
              className={styles.dataPolygon}
            />
            {showAxisDots &&
              secondaryDataPoints?.map((point, i) => (
                <circle
                  key={`secondary-dot-${i}`}
                  cx={point.x}
                  cy={point.y}
                  r={AXIS_DOT_RADIUS}
                  fill={secondaryStrokeColor ?? strokeColor}
                  fillOpacity={AXIS_DOT_FILL_OPACITY}
                  className={styles.axisDot}
                />
              ))}
            {showAxisDots &&
              dataPoints.map((point, i) => (
                <circle
                  key={`dot-${i}`}
                  cx={point.x}
                  cy={point.y}
                  r={AXIS_DOT_RADIUS}
                  fill={strokeColor}
                  fillOpacity={AXIS_DOT_FILL_OPACITY}
                  className={styles.axisDot}
                />
              ))}
            {axisLines.map((line, i) => (
              <line
                key={`hover-${i}`}
                x1={cx}
                y1={cy}
                x2={line.x2}
                y2={line.y2}
                stroke="transparent"
                strokeWidth={HOVER_TARGET_STROKE_WIDTH}
                className={styles.hoverTarget}
                onMouseMove={(e) => {
                  showTooltipForIndex(i, e.clientX, e.clientY);
                }}
                onMouseLeave={clearActiveIndex}
              />
            ))}
          </g>
        </svg>
      </div>

      <div aria-live="polite" className="sr-only">
        {activeIndex !== undefined && data[activeIndex]
          ? `${data[activeIndex].label}: ${data[activeIndex].value}%`
          : ""}
      </div>

      {tooltipOpen && tooltipData && renderTooltip && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          offsetLeft={tooltipPlacement === "left" ? -TOOLTIP_OFFSET.left : TOOLTIP_OFFSET.left}
          offsetTop={TOOLTIP_OFFSET.top}
          style={tooltipPlacement === "left" ? tooltipLeftStyles : tooltipStyles}
          detectBounds={false}
        >
          <ChartTooltip>{renderTooltip(tooltipData)}</ChartTooltip>
        </TooltipInPortal>
      )}
    </Box>
  );
};

RadarChart.displayName = "DS.Charts.RadarChart";

export default memo(RadarChart);
